import type { SidebarDestination } from '@alison/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import { usePreflightOnly } from '../../lib/clientAccess.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';
import { AppShell } from '../AppShell.js';
import { FlightCheckHeader } from './FlightCheckHeader.js';
import { type FlightCheckTab, FlightCheckTabs } from './FlightCheckTabs.js';
import { useNewBatch } from './useViewBatch.js';

interface FlightCheckPageProps {
  // Both come from the service, so they are absent until its first answer.
  title?: string | undefined;
  subtitle?: string | undefined;
  active?: SidebarDestination;
  tab: FlightCheckTab;
  children: ReactNode;
}

export function FlightCheckPage({
  title,
  subtitle,
  active = 'outcomes',
  tab,
  children,
}: FlightCheckPageProps): ReactNode {
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  // Preflight-only clients move between the pages from the sidebar; hidden while the type loads.
  const showOverviewChrome = usePreflightOnly() === false;
  const newBatch = useNewBatch();

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell
      active={active}
      account={account}
      onLogout={() => void onSignOut()}
      sx={{ px: { xs: '24px', md: '40px', xl: '64px' }, py: { xs: '32px', md: '48px' } }}
    >
      <Stack sx={{ gap: '32px', maxWidth: 1520, mx: 'auto' }}>
        <FlightCheckHeader
          title={showOverviewChrome ? title : undefined}
          subtitle={showOverviewChrome ? subtitle : undefined}
          onNewBatch={newBatch}
        />
        {showOverviewChrome ? <FlightCheckTabs value={tab} /> : null}
        {children}
      </Stack>
    </AppShell>
  );
}
